import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { featureFlagsApi } from '../api/featureFlagsApi';
import { useToast } from '../../../shared/components/toast/useToast';
import type {
  CreateFeatureFlagPayload,
  FeatureFlagQuery,
  UpdateFeatureFlagPayload,
} from '../types';

const KEY = 'feature-flags';

/** Nest validation errors arrive as `message: string[]`; take the first line. */
const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

export const useFeatureFlags = (query: FeatureFlagQuery = {}) =>
  useQuery({
    queryKey: [KEY, 'list', query],
    queryFn: () => featureFlagsApi.list(query),
    staleTime: 30 * 1000,
  });

/**
 * Audit history for one flag. Enabled only while its panel is open — a flag list
 * of any size would otherwise fan out to one request per row.
 */
export const useFeatureFlagAuditLog = (id: string | undefined) =>
  useQuery({
    queryKey: [KEY, 'audit', id],
    queryFn: () => featureFlagsApi.auditLog(id as string),
    enabled: !!id,
    staleTime: 30 * 1000,
  });

export const useCreateFeatureFlag = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (payload: CreateFeatureFlagPayload) => featureFlagsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'list'] });
      toast.success('Flag created');
    },
    onError: (err: unknown) => {
      // ApiFetch has already toasted the server message, so only add one when
      // we have something better to say. 409 is the (key, scope, scopeValue)
      // unique index, which deserves a plainer sentence than the Prisma error.
      if ((err as { status?: number })?.status === 409) {
        toast.error('A flag with that key already exists for this scope.');
        return;
      }
      if (!(err as { toasted?: boolean })?.toasted) {
        toast.error(errMsg(err, 'Failed to create flag'));
      }
    },
  });
};

export const useUpdateFeatureFlag = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFeatureFlagPayload }) =>
      featureFlagsApi.update(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'list'] });
      // The write is audit-logged server-side, so an open history panel is stale
      // the moment the mutation lands.
      queryClient.invalidateQueries({ queryKey: [KEY, 'audit', variables.id] });
      toast.success('Flag updated');
    },
    onError: (err: unknown) => {
      if (!(err as { toasted?: boolean })?.toasted) toast.error(errMsg(err, 'Failed to update flag'));
    },
  });
};

export const useDeleteFeatureFlag = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => featureFlagsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'list'] });
      toast.success('Flag deleted');
    },
    onError: (err: unknown) => {
      if (!(err as { toasted?: boolean })?.toasted) toast.error(errMsg(err, 'Failed to delete flag'));
    },
  });
};
